import { SettingType } from '@fluidx/office-fluid-types';
import { safeLocalStorage } from './localStorage';
import { queryParams } from './queryParams';

const settingsCache = new Map<string, SettingType>();

/**
 * Used whenever a setting is not intended to be hosted in ECS. Use `getFluidExperiencesSetting` if setting is intended to be stored in ECS.
 * `getSetting` is also called whenever the settingsProvider for `getFluidExperiencesSetting` is `undefined`.
 *
 * Note: When using query params you can only use `'on'` and `'off'`.
 * @param settingName Name of the setting to retrieve.
 * @param defaultValue Default value to be returned whenever there are no overrides or the setting is not found.
 * @returns The setting's value.
 */
export function getSetting<T extends SettingType>(settingName: string, defaultValue: T): T {
  const settingValue = getAndCacheSettingValue(settingName, defaultValue);
  return settingValue;
}

function getAndCacheSettingValue<T extends SettingType>(settingName: string, defaultValue: T): T {
  // Cache used to store the value of the setting within a session.
  // A page reload should be required after setting a value in localStorage.
  const cacheValue = settingsCache.get(settingName);
  if (cacheValue !== undefined) {
    return cacheValue as T;
  }

  let queryParamValue: SettingType | undefined;
  let localStorageValue: SettingType | undefined;
  let settingValue: SettingType;

  if ((queryParamValue = getSettingFromQueryParam(settingName)) !== undefined) {
    settingValue = queryParamValue;
  } else if ((localStorageValue = getSettingFromLocalStorage(settingName)) !== undefined) {
    settingValue = localStorageValue;
  } else {
    settingValue = defaultValue;
  }

  // Storing the value in the settingsCache for consistency and to avoid subsequent overrides reads
  settingsCache.set(settingName, settingValue);

  return settingValue as T;
}

// TODO: Task 5130567: Contract for hosts to provide settings overrides
function getSettingFromLocalStorage(settingName: string): SettingType | undefined {
  const localStorageValue = safeLocalStorage()?.getItem(settingName);
  return safeSettingValueParse(localStorageValue);
}

// TODO: Task 5130567: Contract for hosts to provide settings overrides
function getSettingFromQueryParam(settingName: string): SettingType | undefined {
  const settingParamValue = queryParams.get(settingName);
  return safeSettingValueParse(settingParamValue);
}

function safeSettingValueParse(settingValue: string | null | undefined): SettingType | undefined {
  if (settingValue !== null && settingValue !== undefined) {
    let value;
    try {
      value = JSON.parse(settingValue);
    } catch (error: any) {
      // catching error to return undefined because JSON.parse could easily throw if string is not formatted as JSON
    }

    // this check is to assign the correct value whenever the settingValue the is not in Json Format
    const valueToReturn = value !== undefined ? value : settingValue;

    // We also allow 'on' and 'off' to represent true and false
    if (valueToReturn === 'on' || valueToReturn === 'off') {
      return valueToReturn === 'on';
    }

    return valueToReturn;
  }

  return undefined;
}
